/* eslint-disable camelcase */
import React, { useCallback, useState, useEffect, useRef } from 'react';
import { func } from 'prop-types';
import classnames from 'classnames';
import {
    middleBannerCampaign as MiddleBannerCampaignUtil,
    getUser,
} from 'utils';
import { Card, withGTM } from 'components';
import {
    useFeatureFlags,
    usePlatform,
    useBaseUrls,
    useListingData,
    useNewUser,
} from 'hooks';
import './MiddleBanner.scss';

const openedPlatforms = ['DE', 'ES', 'FR', 'IT', 'UK'];

function MiddleBanner({ pushToGTM }) {
    const bannerRef = useRef(null);
    const rootClassnames = classnames('middle-banner');
    const [
        {
            hasCampaignManagerFF,
            hasMiddleBannerFF,
            hasMiddleBannerABTest,
            hasCampaignManagerABTest,
        },
        { exists },
    ] = useFeatureFlags({
        hasCampaignManagerFF: 'ff.promotion.campaignmanager',
        hasMiddleBannerFF: 'ff.promotion.midbanner.listing',
        hasMiddleBannerABTest: 'promotion.midbanner.abtest',
        hasCampaignManagerABTest: 'promotion.campaignmanager.abtest',
    });
    const existsCampaignManagerABTest = exists(
        'promotion.campaignmanager.abtest',
    );

    const platform = usePlatform().toUpperCase();
    const isOpenedPlatform = openedPlatforms.some(
        openedPlatform => platform === openedPlatform,
    );
    const user = getUser();
    const isNewUser = useNewUser();
    const market = user && user.isB2B ? 'B2B' : 'B2C';
    const baseUrls = useBaseUrls();

    const shouldDisplayMiddleBanner =
        hasCampaignManagerFF &&
        hasMiddleBannerFF &&
        hasMiddleBannerABTest &&
        (!existsCampaignManagerABTest || hasCampaignManagerABTest) &&
        isOpenedPlatform;

    const [campaignData, setData] = useState({});

    const sendToGTM = useCallback(() => {
        if (pushToGTM) {
            pushToGTM({
                event: 'click_campaign',
                campaign_id: campaignData?.campaign_uuid,
                campaign_name: campaignData?.name,
                space_slug: 'middle_banner',
            });
        }
    }, [campaignData]);

    // Only register GTM display_campaign event when MiddleBanner is on viewport
    const registerObserver = useCallback(data => {
        if (!bannerRef.current) return;

        const options = { threshold: 1.0 };
        const callback = ([entry], observer) => {
            if (entry.intersectionRatio === 1) {
                pushToGTM({
                    event: 'display_campaign',
                    campaign_id: data?.campaign_uuid,
                    campaign_name: data?.name,
                    space_slug: 'middle_banner',
                });
                observer.disconnect();
            }
        };

        const observer = new IntersectionObserver(callback, options);
        observer.observe(bannerRef.current);
    }, []);

    const { listingData } = useListingData();

    useEffect(() => {
        if (shouldDisplayMiddleBanner) {
            MiddleBannerCampaignUtil.fetchCampaign(
                listingData,
                baseUrls.browserAPI,
                platform,
                market,
                user,
                isNewUser,
            ).then(result => {
                if (result.campaignData?.name) {
                    setData(result.campaignData);
                    registerObserver(result.campaignData);
                }
            });
        }
    }, []);

    return campaignData?.name ? (
        <Card
            ref={bannerRef}
            className={rootClassnames}
            id="middle_banner"
            onClick={sendToGTM}
        >
            <a href={campaignData.redirection_url}>
                <picture>
                    <source
                        media="(max-width: 767px)"
                        srcSet={campaignData.creative_mobile.url}
                    />
                    <img alt="" src={campaignData.creative_desktop.url} />
                </picture>
            </a>
        </Card>
    ) : null;
}

MiddleBanner.propTypes = {
    pushToGTM: func.isRequired,
};

export default withGTM(MiddleBanner);
